import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import NotificationSystem from 'react-notification-system';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import moment from 'moment';
import { Row, Col } from 'react-bootstrap';
import SlidingPane from 'react-sliding-pane';
import Toggle from 'react-toggle';
import forEach from 'lodash/forEach';

import Header from 'components/Header';
import { Grid, Column } from 'components/Layout';
import Input from 'components/Form/Input';
import Card from 'components/Card';
import Select from 'components/Form/Select';
import FormGroup from 'components/Form/FormGroup';
import { Button } from 'components/Button';
import Validator from 'components/Validator/Validator';
import {
  getApplications,
  updateApplication,
  getRefundReasons,
  getActiveContract,
  refundRequest,
} from 'actions/application';
import { getAdditionalSavingAmount } from 'actions/workflow';

import { parseUrlParams } from 'utils/parseUrlParams';
import { formatCurrency } from 'utils/formatCurrency';
import { toTitleCase } from 'utils/toTitleCase';
import { states } from 'utils/states';
import { currencyMask, floatUnmask, unmaskCurrency } from 'utils/masks';
import closeIcon from 'assets/icons/arrow-left-grey.svg';
import schema from './schema';
import style from './style.scss';

const ApplicationDetail = (props) => {
  const { validator: { values, errors } } = props;
  const dispatch = useDispatch();
  const params = parseUrlParams(window.location.search);
  const [clientId, setClientId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalShown, setIsModalShown] = useState(false);
  const [additionalSavingsAmount, setAdditionalSavingsAmount] = useState(null);
  const [isPaneOpen, setIsPaneOpen] = useState(false);
  const [refund, setRefund] = useState(null);
  const [refundReasons, setRefundReasons] = useState([]);
  const [contract, setContract] = useState(null);
  const [isFull, setIsFull] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isEditingAgent, setIsEditingAgent] = useState(false);
  const [isAgentSaving, setIsAgentSaving] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [response, setResponse] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const notificationSystem = useRef(null);
  const form = useRef(null);

  const showMessage = (message, type, position) => {
    notificationSystem.current
    && notificationSystem.current.addNotification({
      message,
      level: type,
      position,
    });
  };

  const getApplicationDetail = () => {
    dispatch(getApplications({
      url: `/applications/${params.applicationId}`,
      success: (res) => {
        setResponse(res);
        setClientId(get(res, 'channel.attributes.clientId'));
      },
      fail: (err) => { // eslint-disable-line
        if (err.response) {
          showMessage('fetching user detail failes', 'error', 'tc');
        }
      },
    }));
  };

  const loadRefunds = () => {
    const merchantId = localStorage.getItem('merchantId');
    dispatch(getRefundReasons({
      url: `/refunds/search?merchantId=${merchantId}&applicationId=${params.applicationId}&refundStatus=6008&sortBy=createdOn&sortOrder=desc`,
      success: (res) => {
        if (res && res.data.length > 0) {
          setRefund(res.data[0]);
        }
      },
      fail: (err) => {
        if (err.response) {
          notificationSystem.current
          && notificationSystem.current.addNotification({
            message: 'fetching refund reasons',
            level: 'error',
            position: 'tc',
          });
        }
      },
    }));
  };

  const loadRefundReasons = () => {
    dispatch(getRefundReasons({
      url: '/lookup/refund-reasons',
      success: (res) => {
        const reasons = [];
        forEach(res, (reason) => {
          if (get(reason, 'attributes.channel') === 'fsl') {
            reasons.push({
              title: reason.label,
              value: reason.code,
            });
          }
        });
        setRefundReasons(reasons);
      },
      fail: (err) => {
        if (err.response) {
          notificationSystem.current
          && notificationSystem.current.addNotification({
            message: 'fetching refund reasons',
            level: 'error',
            position: 'tc',
          });
        }
      },
    }));
  };

  const loadActiveContract = () => {
    dispatch(getActiveContract({
      url: `/offers/application/${params.applicationId}/contract/active`,
      success: (res) => {
        setContract(res);
      },
      fail: (err) => {
        if (err.data) {
          notificationSystem.current
          && notificationSystem.current.addNotification({
            message: err.data.message,
            level: 'error',
            position: 'tc',
          });
        }
      },
    }));
  };

  const loadRefundData = () => {
    loadRefunds();
    loadRefundReasons();
    loadActiveContract();
  };

  const getApplicationStatus = () => {
    dispatch(getApplications({
      url: `/applications-view/search?sortBy=applicationDate&sortOrder=desc&channelCode=fsl&ApplicationId=${params.applicationId}`,
      success: (res) => {
        const applicationStatusTemp = get(res, 'data.0.statusAliases.merchantPortal');
        const isRefund = applicationStatusTemp === 'Funded';
        setApplicationStatus(applicationStatusTemp);

        if (get(res, 'data.0.statusLabel') === 'Declined') {
          dispatch(getAdditionalSavingAmount({
            data: {},
            url: `/workflows/application/${params.applicationId}/workflow/fsl1/additional-saving-amount`,
            success: (res2) => {
              setAdditionalSavingsAmount(get(res2, 'additionalSavingAmount') || null);
            },
            fail: (error) => { // eslint-disable-line
            },
          }));
        }

        if (isRefund) {
          loadRefundData();
        }
      },
      fail: (error) => { // eslint-disable-line
      },
    }));
  };

  useEffect(() => {
    getApplicationDetail();
    getApplicationStatus();
  }, []);

  const getCurrentAmountFinanced = () => {
    let currentAmountFinanced = 0;
    if (refund) {
      currentAmountFinanced = refund.newAmountFinanced || 0;
    } else if (contract) {
      currentAmountFinanced = contract.amountTaken || 0;
    }
    return parseFloat(currentAmountFinanced);
  };

  const handleBlur = (event) => {
    event.preventDefault();
    const { validator: { onChangeHandler } } = props;
    switch (event.target.name) {
      case 'refundAmount':
        onChangeHandler(event.target.name, formatCurrency(floatUnmask(event.target.value || '0'), 2));
        break;
      default:
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
    }
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    const currentAmountFinanced = getCurrentAmountFinanced();

    switch (event.target.name) {
      case 'refundAmount':
        onChangeHandler(event.target.name, event.target.value);
        setIsFull(parseFloat(floatUnmask(event.target.value)) === currentAmountFinanced);
        break;
      case 'clientId':
        setClientId(event.target.value);
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
        break;
    }
  };

  const handleCloseClick = (e) => {
    e.preventDefault();
    setIsModalShown(false);
  };

  const handleSave = (e) => {
    e.preventDefault();
    setIsSaving(true);
    const data = {
      channel: {
        attributes: {
          clientId: clientId && clientId.trim().replace(/\s\s+/g, ' '),
        },
      },
    };

    dispatch(updateApplication({
      url: `/applications/${params.applicationId}`,
      data,
      success: (res) => { // eslint-disable-line
        setIsSaving(false);
        getApplicationDetail();
        notificationSystem.current
        && notificationSystem.current.addNotification({
          message: 'Successfully saved Client ID!',
          level: 'success',
          position: 'tc',
        });
      },
      fail: (error) => { // eslint-disable-line
        setIsSaving(false);
        setClientId(get(response, 'channel.attributes.clientId'));
        notificationSystem.current
        && notificationSystem.current.addNotification({
          message: (error.data && error.data.message) || 'Please try again.',
          level: 'error',
          position: 'tc',
        });
      },
    }));
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setClientId(get(response, 'channel.attributes.clientId'));
  };

  const handleSubmitForm = (data, e) => {
    e.preventDefault();
    const { validator: { validate } } = props;

    if (validate(schema).isValid) {
      const formData = {
        ...data,
        refundAmount: floatUnmask(data.refundAmount),
        signature: data.signature,
      };

      if (params.applicationId) {
        setIsSubmitted(true);
        dispatch(refundRequest({
          url: '/refunds',
          data: {
            applicationId: params.applicationId,
            refundRequestedAmount: parseFloat(unmaskCurrency(formData.refundAmount)),
            refundReason: formData.reasonForRefund,
            signature: formData.signature,
            consentAutomaticDebit: true,
          },
          success: (response) => { // eslint-disable-line
            setIsPaneOpen(false);
            setIsSubmitted(false);
            notificationSystem.current
            && notificationSystem.current.addNotification({
              message: 'Refund request has been successfully submitted',
              level: 'success',
              position: 'tc',
            });
          },
          fail: (error) => { // eslint-disable-line
            setIsPaneOpen(true);
            setIsSubmitted(false);
            notificationSystem.current
            && notificationSystem.current.addNotification({
              message: (error.data && error.data.message) || 'Refund request submission failed',
              level: 'error',
              position: 'tc',
            });
          },
        }));
      }
    }
  };

  const handleToggleChange = (event) => {
    // eslint-disable-next-line prefer-destructuring
    const checked = event.target.checked;
    setIsFull(event.target.checked);
    if (checked) {
      const amountFinanced = getCurrentAmountFinanced().toString();
      const { validator: { onChangeHandler } } = props;
      onChangeHandler('refundAmount', formatCurrency(floatUnmask(amountFinanced || '0'), 2));
    }
  };

  const handleEditAgent = (e) => {
    e.preventDefault();
    const { validator: { setValues } } = props;

    setValues({
      agent: get(response, 'agent'),
    });
    setIsEditingAgent(true);
  };

  const handleSaveAgent = () => {
    setIsAgentSaving(true);

    const data = {
      agent: values.agent,
    };

    dispatch(updateApplication({
      url: `/applications/${params.applicationId}`,
      data,
      success: (res) => {
        console.log('res = ', res);
        setIsAgentSaving(false);
        setIsEditingAgent(false);
        getApplicationDetail();
        notificationSystem.current
        && notificationSystem.current.addNotification({
          message: 'Successfully updated Agent!',
          level: 'success',
          position: 'tc',
        });
      },
      fail: (error) => {
        console.log('error = ', error);
        setIsAgentSaving(false);
        setIsEditingAgent(false);

        notificationSystem.current
        && notificationSystem.current.addNotification({
          message: (error.data && error.data.message) || 'Please try again.',
          level: 'error',
          position: 'tc',
        });
      },
    }));
  };

  const handleCancelSaveAgent = (event) => {
    event.preventDefault();
    const { validator: { onChangeHandler } } = props;

    setIsEditingAgent(false);
    setIsAgentSaving(false);

    onChangeHandler(event.target.name, get(response, 'agent'));
  };

  const renderRequestRefundTray = () => {
    const { validator: { isValid } } = props;

    if (form && form.current && !isValid && isSubmitted) {
      const errs = Object.keys(errors);
      form.current[errs[0]].focus();
      setIsSubmitted(false);
    }

    const currentAmountFinanced = getCurrentAmountFinanced();

    return (
      <SlidingPane
        isOpen={isPaneOpen}
        closeIcon={<img src={closeIcon} alt="close" />}
        title="Return of Funds"
        width="460px"
        onRequestClose={() => {
          setIsPaneOpen(false);
        }}
        from="right"
      >
        <form className={cn('requestRefund')} onSubmit={handleSubmitForm.bind(null, values)} ref={form}>
          <div className="grid-x grid-margin-x d-flex flex-column" style={{ minHeight: '100%' }}>
            <div className="cell small-12">
              <Input
                type="tel"
                label="Return Amount"
                name="refundAmount"
                value={values.refundAmount}
                isMasked={currencyMask}
                onChange={handleInputChange}
                hasError={!!errors.refundAmount}
                isRequired
                onBlur={handleBlur}
                errorMessage={errors.refundAmount}
              />
            </div>
            <div className="cell small-12 mb-4">
              <div className="containerToggle d-flex">
                <Toggle
                  checked={isFull}
                  icons={false}
                  onChange={handleToggleChange}
                />
                <span style={{ marginLeft: 5 }}>Full</span>
              </div>
            </div>
            <div className="cell small-12 mb-5">
              <Select
                name="reasonForRefund"
                data={refundReasons}
                value={values.reasonForRefund}
                onChange={handleInputChange}
                label="Reason for Refund"
                isRequired
                hasError={!!errors.reasonForRefund}
                errorMessage={errors.reasonForRefund}
              />
            </div>
            <div className="cell small-12">
              <label className="has-value">
                <span>Current Amount Financed</span>
                <input
                  type="text"
                  value={`$ ${formatCurrency(currentAmountFinanced, 2)}`}
                  readOnly
                />
              </label>
            </div>
            <div className="cell small-12">
              <Input
                label="Signature - Type Full Name"
                name="signature"
                value={values.signature}
                onChange={handleInputChange}
                isRequired
                hasError={!!errors.signature}
                onBlur={handleBlur}
                errorMessage={errors.signature}
              />
            </div>
            <div className="cell small-12 mt-auto">
              <Button
                id="SubmitRefundRequest"
                className="button green w-100"
                isLoading={isSubmitted}
              >
                Return Funds
              </Button>
            </div>
          </div>
        </form>
      </SlidingPane>
    );
  };

  const handleEnter = () => {
    if (!isHover) {
      setIsHover(true);
    }
  };

  const handleLeave = () => {
    setIsHover(false);
  };

  return (
    <Fragment>
      <NotificationSystem ref={notificationSystem} />
      <Header />
      <section className="container section">
        <div className="grid-container fluid portal page-application-details">

          <Grid>
            <Column className="small-12 card-grid-container">

              <Grid className={cn('grid-margin-x page-header', style.pageHeader)}>
                <Column className="small-12 large-4 d-flex">
                  <h4 className={style.noBottomMargin}>Application ID:&nbsp;</h4>
                  <h4 className={style.noBottomMargin} style={{ fontWeight: 300 }}>{response && response.applicationId}</h4>
                </Column>
                <Column className="small-12 large-3">
                  <div className="loan-status">
                    <span>Status</span>
                    <span className={cn('badge needs-sig', style.activeStatus, style[`status-${get(response, 'status.code')}`])}>{applicationStatus}</span>
                  </div>
                </Column>
                <Column className="small-12 large-5 d-flex justify-content-end">
                  {additionalSavingsAmount && (
                    <div className={style.declineStyle}>
                      Borrower can reapply with Additional Savings Amount in CDA: $ {formatCurrency(additionalSavingsAmount, 2) || '-'}
                    </div>
                  )}
                  <div className="d-flex align-items-center justify-content-end w-100 flex-gap-1">
                    <Grid>
                      {isEditingAgent ? (
                        <Column className="small-12 large-12">
                          <Grid>
                            <Column className="small-12 large-6">
                              <Input
                                label="Agent"
                                name="agent"
                                value={get(values, 'agent') || ''}
                                onChange={handleInputChange}
                                isBadgeVisible={false}
                                hasError={!!errors.agent}
                                onBlur={handleBlur}
                              />
                            </Column>
                            <Column className="small-12 large-3">
                              <Button
                                className={cn('button green', style.noBottomMargin)}
                                onClick={handleSaveAgent}
                                isLoading={isAgentSaving}
                                style={{ paddingTop: isAgentSaving ? 9 : 12, paddingBottom: isAgentSaving ? 9 : 12, marginLeft: 10 }}
                              >
                                Save
                              </Button>
                            </Column>
                            <Column className="small-12 large-3">
                              <Button
                                className={cn('button red', style.noBottomMargin)}
                                onClick={handleCancelSaveAgent}
                                isLoading={isAgentSaving}
                                style={{ paddingTop: isAgentSaving ? 9 : 12, paddingBottom: isAgentSaving ? 9 : 12 }}
                              >
                                Cancel
                              </Button>
                            </Column>
                          </Grid>
                        </Column>
                      ) : (
                        <Column className="small-12 d-flex">
                          <h4 className={style.noBottomMargin}>Agent:</h4>
                          <h4
                            className={style.noBottomMargin}
                            style={{ fontWeight: 300, position: 'relative' }}
                            onMouseMove={handleEnter}
                            onMouseLeave={handleLeave}
                            onClick={handleEditAgent}
                          >
                            &nbsp;{get(response, 'agent') || '-'}
                            {isHover && <span className={cn('glyphicon glyphicon-edit', style['edit-icon'])} />}
                          </h4>
                        </Column>
                      )}
                    </Grid>
                    {applicationStatus === 'Funded' && (
                      <Button
                        className={cn('button green m-0', style.requestFund)}
                        onClick={() => {
                          setIsPaneOpen(true);
                        }}
                      >
                        Return Funds
                      </Button>
                    )}
                  </div>
                </Column>
              </Grid>

              <Grid className={cn('grid-margin-x page-header', style.pageHeader)}>
                <Column className="small-12 large-4">
                  <Card title="Personal Information" isSectionWrapped isEditable>
                    <Input
                      label="First Name"
                      name="firstName"
                      value={get(response, 'applicant.firstName') || ''}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Input
                      label="Last Name"
                      name="lastName"
                      value={get(response, 'applicant.lastName') || ''}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Input
                      label="Middle Name"
                      name="middleName"
                      value={get(response, 'applicant.middleName') || ''}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Input
                      label="Last 4 SSN"
                      name="last4ssn"
                      value={`${get(response, 'applicant.ssn') || ''}`.slice(-4)}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Input
                      label="Date of Birth"
                      name="dateOfBirth"
                      value={moment(get(response, 'applicant.dateOfBirth')).format('MM/DD/YYYY')}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Input
                      label="Client ID"
                      name="clientId"
                      value={clientId || ''}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Input
                      label="Reference ID"
                      name="referenceId"
                      value={get(response, 'referral.referenceId') || ''}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Row className="justify-content-between" hidden>
                      <Col sm={6} className="pb-3">
                        <Button
                          className={cn('button green w-100', style.button)}
                          onClick={handleSave}
                          isLoading={isSaving}
                          isDisabled={isSaving || !clientId || !clientId.trim() || get(response, 'channel.attributes.clientId') === clientId}
                        >
                          Save
                        </Button>
                      </Col>
                      <Col sm={6} className="pb-3">
                        <Button
                          className={cn('button green w-100', style.button)}
                          onClick={handleCancel}
                          isLoading={isSaving}
                        >
                          Cancel
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                </Column>

                <Column className="small-12 large-4">
                  <Card title="Borrower Address" isSectionWrapped isEditable>
                    <p>Address of the primary borrower</p>
                    <Input
                      label="Street Address"
                      name="streetAddress"
                      value={get(response, 'applicant.addresses.0.address') || ''}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Input
                      label="Street Address 2"
                      name="streetAddress"
                      value={get(response, 'applicant.addresses.0.address') || ''}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Input
                      label="City"
                      name="city"
                      value={get(response, 'applicant.addresses.0.city') || ''}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Grid className="grid-margin-x page-header">
                      <Column className="small-7">
                        <Select
                          name="applicant.addresses.state"
                          data={states}
                          value={get(response, 'applicant.addresses.0.state')}
                          onChange={handleInputChange}
                          label="State"
                          isRequired
                          readOnly
                        />
                      </Column>
                      <Column className="small-5">
                        <Input
                          label="Zip Code"
                          name="zipCode"
                          value={get(response, 'applicant.addresses.0.zipCode')}
                          onChange={handleInputChange}
                          isBadgeVisible={false}
                          readOnly
                        />
                      </Column>
                    </Grid>
                  </Card>
                </Column>

                <Column className="small-12 large-4">
                  <Card title="Contact Information" isSectionWrapped isEditable>
                    <p>Approval and application status will be sent to:</p>
                    <Input
                      label="Email Address"
                      name="email"
                      value={get(response, 'applicant.email')}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                    <Input
                      label="Mobile Phone"
                      name="phoneNumber"
                      value={get(response, 'applicant.phoneNumbers.0.number')}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                    />
                  </Card>
                  <Card title="Funding Information" isSectionWrapped isEditable>
                    <Input
                      label="Requested Loan Amount"
                      name="requestLoanAmount"
                      value={`${formatCurrency(get(response, 'requestedAmount') || 0, 2)}`}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                      isMasked={currencyMask}
                    />
                    <Input
                      label="CDA Balance"
                      name="cdaBalance"
                      value={`${formatCurrency(get(response, 'channel.attributes.customerDedicatedAccountBalance') || 0, 2)}`}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                      isMasked={currencyMask}
                    />
                    <Input
                      label="Monthly Savings Amount"
                      name="monthlySavingsAmount"
                      value={`${formatCurrency(get(response, 'channel.attributes.monthlySavingsAmount') || 0, 2)}`}
                      onChange={handleInputChange}
                      isBadgeVisible={false}
                      readOnly
                      isMasked={currencyMask}
                    />
                  </Card>
                </Column>
              </Grid>

            </Column>
          </Grid>

        </div>
      </section>
      <div className={cn('reveal-overlay', isModalShown && style.modal)}>
        <div className="reveal" id="LoanCancellation" data-reveal="" data-close-on-click="true" data-animation-in="slide-in-up" data-animation-out="slide-out-down" role="dialog" aria-hidden="false" data-yeti-box="LoanCancellation" data-resize="LoanCancellation" data-e="30lef0-e" tabIndex="-1" style={{ display: 'block' }}>
          <div className="card-header">
            <h5>Cancellation Reason</h5>
          </div>

          <Grid>
            <Column className="small-12">
              <FormGroup>
                <Grid className="grid-margin-x">

                  <Column className="small-12 medium-6">
                    <Button
                      className="button secondary toggle-btn w-100"
                    >
                      Interest Rate
                    </Button>
                  </Column>
                  <Column className="small-12 medium-6">
                    <Button className="button secondary toggle-btn w-100">Fees</Button>
                  </Column>
                  <Column className="small-12 medium-6">
                    <Button className="button secondary toggle-btn w-100">No longer making purchase</Button>
                  </Column>
                  <Column className="small-12 medium-6">
                    <Button className="button secondary toggle-btn w-100">Unable to Contact</Button>
                  </Column>
                  <Column className="small-12 medium-6">
                    <Button className="button secondary toggle-btn w-100">Monthly payment too high</Button>
                  </Column>
                  <Column className="small-12 medium-6">
                    <Button className="button secondary toggle-btn w-100">Wanted same as cash</Button>
                  </Column>
                  <Column className="small-12 medium-6">
                    <Button className="button secondary toggle-btn other-btn w-100">Other...</Button>
                  </Column>
                  <Column className="small-12 hide" id="OtherReason">
                    <Input
                      label="Enter Other Reason"
                      id="LoanSignature"
                      required=""
                    />
                  </Column>
                </Grid>

                <Button id="FormSubmit2" className="button large arrow green w-100" disabled="">Cancel Contract</Button>
              </FormGroup>
            </Column>
          </Grid>

          <Button className="close-button" onClick={handleCloseClick} data-close="" aria-label="Close reveal" type="button">
            <span aria-hidden="true">×</span>
          </Button>
        </div>
      </div>
      {renderRequestRefundTray()}
    </Fragment>
  );
};

ApplicationDetail.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default compose(
  Validator(schema),
  withRouter
)(ApplicationDetail);



// WEBPACK FOOTER //
// ./src/containers/pages/ApplicationDetail/index.js